import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Pencil, AlertTriangle, Receipt as ReceiptIcon, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { StatusBadge, StatusSelect } from "@/components/StatusSelect";
import { PaymentDialog } from "@/components/PaymentDialog";
import { api } from "@/lib/api";
import { inr } from "@/lib/calc";
import { STATUSES } from "@/lib/status";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/hooks";
import { ReferralThanks } from "@/components/ReferralThanks";
import { SpotCountdown } from "@/components/SpotCountdown";
import { SpotDecisionDialog, DECISION } from "@/components/SpotDecision";
import { NotInterestedDialog, hasPayments } from "@/components/NotInterested";
import { admissionDue, admissionWarn, formFeeWarn, partialCfg, partsOf } from "@/lib/spot";

const fmt = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-IN");
const errMsg = (e, d) => e?.response?.data?.detail?.toString?.() || d;

const FormFee = ({ r, onSaved }) => {
  const [open, setOpen] = useState(false);
  const f = r.form_fee;
  const save = async (pay) => {
    try {
      const res = await api.patch(`/receipts/${r.id}/form-fee`, pay);
      onSaved(res.data);
      toast.success("Form fee recorded");
      setOpen(false);
    } catch (e) { toast.error(errMsg(e, "Could not record form fee")); }
  };
  return (
    <>
      {f ? (
        <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-full border border-plum-200 bg-white px-2.5 py-1 text-xs text-plum-900 hover:bg-plum-50" data-testid="form-fee-summary">
          <ReceiptIcon className="h-3.5 w-3.5" />Form fee ₹ {inr(f.amount)} · {f.mode} · {fmt(f.date)}
        </button>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="border-gold-500 text-plum-900" data-testid="record-form-fee-btn">
          <ReceiptIcon className="mr-1.5 h-3.5 w-3.5" />Record form fee
        </Button>
      )}
      <PaymentDialog open={open} onOpenChange={setOpen} receiptNo={r.receipt_no} defaultAmount={1000} initial={f}
        onConfirm={save} warnFor={formFeeWarn(r)} title="Form fee received" confirmLabel="Save form fee" />
    </>
  );
};

export const ReceiptStatusBar = ({ id }) => {
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const [partOpen, setPartOpen] = useState(false);
  const [decide, setDecide] = useState(null);
  const [lostOpen, setLostOpen] = useState(false);
  const { isAdmin } = useAuth();
  const { data: settings } = useSettings();
  const { data: r } = useQuery({ queryKey: ["receipt", id], queryFn: () => api.get(`/receipts/${id}`).then((x) => x.data) });
  if (!r) return null;

  const saved = (data) => { qc.setQueryData(["receipt", id], data); qc.invalidateQueries({ queryKey: ["receipts"] }); };
  const setStatus = async (status, payment, extra = {}) => {
    try {
      const res = await api.patch(`/receipts/${id}/status`, { status, follow_up_date: r.follow_up_date || "", follow_up_note: r.follow_up_note || "", payment, ...extra });
      saved(res.data);
      toast.success(`${r.receipt_no} marked ${STATUSES.find((s) => s.key === status).label}`);
      setPayOpen(false);
      setPartOpen(false);
      setLostOpen(false);
    } catch (e) { toast.error(errMsg(e, "Could not update status")); }
  };
  const savePartial = ({ payment, only_form_fee, balance_date }) =>
    setStatus("partial", null, { partial_payment: payment, only_form_fee, follow_up_date: balance_date });
  const onSelect = (s) => {
    if (s === "not_interested") return hasPayments(r) ? toast.error("Payments are recorded on this receipt - remove them before marking Not Interested") : setLostOpen(true);
    return s === "admitted" ? setPayOpen(true) : s === "partial" ? setPartOpen(true) : setStatus(s);
  };
  const parts = partsOf(r);
  const undoPart = async () => {
    try { saved((await api.delete(`/receipts/${id}/part-payments/last`)).data); toast.success("Last part-payment removed"); }
    catch (e) { toast.error(errMsg(e, "Could not remove part-payment")); }
  };

  const admitted = r.status === "admitted";
  const p = r.payment;
  const issues = admitted && r.inputs?.spot ? p?.spot_issues || [] : [];
  return (
    <div className={`no-print mb-4 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${admitted ? "border-plum-200 bg-plum-50" : "border-plum-900/10 bg-white"}`} data-testid="receipt-status-bar">
      <span className="text-xs uppercase tracking-wider text-slate-500">Status</span>
      <StatusBadge status={r.status} testid="receipt-status-badge" />
      <SpotCountdown r={r} testid="receipt-spot-countdown" remind />
      {admitted && p && (
        <span className="text-xs text-plum-900" data-testid="receipt-payment-summary">
          ₹ {inr(p.amount)} via {p.mode}{p.reference ? ` · Ref ${p.reference}` : ""} · {fmt(p.date)}
          {parts.length > 0 && ` · incl. ${parts.length} earlier part-payment${parts.length > 1 ? "s" : ""} (₹ ${inr(parts.reduce((t, x) => t + Number(x.amount || 0), 0))})`}
        </span>
      )}
      {r.status === "partial" && (
        <span className="flex items-center gap-1.5 text-xs text-orange-900" data-testid="receipt-partial-summary">
          <Hourglass className="h-3.5 w-3.5" />
          {parts.length ? `${parts.length} part-payment${parts.length > 1 ? "s" : ""} · ₹ ${inr(parts.reduce((t, x) => t + Number(x.amount || 0), 0))} paid` : "Only form fee paid"} · Balance ₹ {inr(admissionDue(r) || 0)}{r.follow_up_date ? ` by ${fmt(r.follow_up_date)}` : ""}
          {parts.length > 0 && <button onClick={undoPart} className="ml-1 font-semibold underline" data-testid="undo-part-btn">Undo last</button>}
        </span>
      )}
      {r.status !== "not_interested" && <FormFee r={r} onSaved={saved} />}
      {r.status === "not_interested" && (
        <span className="text-xs text-slate-600" data-testid="receipt-lost-reason"><b>Not interested:</b> {r.lost_reason || "—"}{r.lost_by ? ` · ${r.lost_by}` : ""}{r.lost_at ? ` · ${new Date(r.lost_at).toLocaleDateString("en-IN")}` : ""}</span>
      )}
      {admitted && r.inputs?.sub?.referral && r.inputs.referralMobile && <ReferralThanks r={r} school={settings?.school_name || "our school"} onSaved={saved} />}
      {issues.length > 0 && (() => {
        const d = p.spot_decision;
        const D = d && DECISION[d.decision];
        return (
          <Popover>
            <PopoverTrigger className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${D ? D.cls : "bg-amber-100 text-amber-900 hover:bg-amber-200"}`} data-testid="spot-rule-flag">
              {D ? <D.Icon className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {D ? D.label : `Spot rule not met (${issues.length}) · awaiting admin`}
            </PopoverTrigger>
            <PopoverContent className="w-80 text-xs text-amber-900" data-testid="spot-rule-details">
              <ul className="list-disc space-y-1 pl-4">{issues.map((x) => <li key={x}>{x}</li>)}</ul>
              <p className="mt-2 border-t border-amber-100 pt-2 text-slate-700" data-testid="spot-rule-reason"><b>Reason:</b> {p.spot_note || "—"}</p>
              {d && (
                <p className="mt-2 border-t border-amber-100 pt-2 text-slate-700" data-testid="spot-decision-detail">
                  <b className={d.decision === "approved" ? "text-emerald-700" : "text-red-700"}>{d.decision === "approved" ? "Approved" : "Rejected"}</b> by {d.by} on {new Date(d.at).toLocaleDateString("en-IN")}: {d.comment}
                </p>
              )}
              {isAdmin && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="h-7 flex-1 bg-emerald-700 text-xs hover:bg-emerald-800" onClick={() => setDecide("approved")} data-testid="spot-approve-btn">{d ? "Change to approve" : "Approve"}</Button>
                  <Button size="sm" className="h-7 flex-1 bg-red-600 text-xs hover:bg-red-700" onClick={() => setDecide("rejected")} data-testid="spot-reject-btn">{d ? "Change to reject" : "Reject"}</Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        );
      })()}
      {issues.length > 0 && p.spot_decision?.decision === "rejected" && (
        <div className="basis-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="spot-rejected-banner">
          <b>Admin rejected the Spot discount.</b> Switch the Category to <b>Revisit</b> and update the receipt so the fees are recalculated.
          {window.location.pathname !== "/" && <a href={`/?edit=${r.id}`} className="ml-1 font-semibold underline" data-testid="spot-rejected-edit-link">Edit receipt</a>}
        </div>
      )}
      {decide && <SpotDecisionDialog r={r} decision={decide} onClose={() => setDecide(null)} onSaved={saved} />}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <StatusSelect value={r.status} onChange={onSelect} testid="receipt-status-select" />
        {admitted ? (
          <Button size="sm" variant="outline" onClick={() => setPayOpen(true)} data-testid="edit-payment-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" />{p ? "Edit payment" : "Add payment"}</Button>
        ) : (
          <>
          <Button size="sm" variant="outline" onClick={() => setPartOpen(true)} className="border-orange-300 text-orange-800 hover:bg-orange-50" data-testid="mark-partial-btn">
            <Hourglass className="mr-1.5 h-3.5 w-3.5" />{r.status === "partial" ? "Add part-payment" : "Partially Paid"}
          </Button>
          <Button size="sm" onClick={() => setPayOpen(true)} className="bg-plum-700 hover:bg-plum-800" data-testid="mark-admitted-btn">
            <BadgeCheck className="mr-1.5 h-4 w-4" />Mark as Admitted
          </Button>
          </>
        )}
      </div>
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} receiptNo={r.receipt_no} defaultAmount={admissionDue(r)}
        initial={p} onConfirm={(pay) => setStatus("admitted", pay)}
        warnFor={admissionWarn(r)} requireNote editHref={window.location.pathname === "/" ? null : `/?edit=${r.id}`} />
      <PaymentDialog open={partOpen} onOpenChange={setPartOpen} receiptNo={r.receipt_no} defaultAmount=""
        onConfirm={savePartial} partial={partialCfg(r)} title="Partially paid" confirmLabel="Save part-payment" />
      <NotInterestedDialog open={lostOpen} onOpenChange={setLostOpen} receiptNo={r.receipt_no} onConfirm={(reason) => setStatus("not_interested", null, { lost_reason: reason, follow_up_date: "" })} />
    </div>
  );
};
